// TEXTMESSAGEPAGE ON THE FRONTEND AND NOT BACKEND!!!

// src/pages/TextMessagesPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { debounce } from 'lodash';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaEye,
  FaRedo,
  FaEdit,
  FaTrashAlt,
  FaTags,
  FaPlus,
  FaSearch,
  FaFilter,
  FaSort,
  FaStar,
  FaBookmark,
} from 'react-icons/fa';

const api = axios.create({
  baseURL: 'http://localhost:5000/api', // Adjust this if your backend is on a different port
});

const TextMessagesPage = () => {
  const { restaurantId } = useParams();
  const [textMessages, setTextMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  // const [params, setParams] = useState({
  //   page: 1,
  //   limit: 10,
  //   sortBy: 'scheduledDate',
  //   sortOrder: 'desc',
  //   status: 'all',
  //   search: '',
  // });

  // const [messages, setMessages] = useState([]);
  // const [loading, setLoading] = useState(true);
  // const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [messagesPerPage, setMessagesPerPage] = useState(10);

  // // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState({ start: null, end: null });

  // // State for sorting
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const [savedMessages, setSavedMessages] = useState([]);
  const [savedFilter, setSavedFilter] = useState('all');

  const fetchMessages = async () => {
    try {
      setLoading(true);
      // const response = await getTextMessages(restaurantId, params);
      const token = localStorage.getItem('ownerToken');
      const response = await axios.get(
        `/api/restaurants/${restaurantId}/messages`,
        // { params }
        {
          params: {
            page: currentPage,
            limit: messagesPerPage,
            sortBy: sortField,
            sortOrder: sortDirection,
            status: statusFilter,
            search: searchTerm,
            startDate: dateFilter.start,
            endDate: dateFilter.end,
            saved: savedFilter === 'saved' ? true : undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log(response.data);

      setTextMessages(response.data.messages);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalMessages: response.data.totalMessages,
      });
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to fetch messages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedMessages = async () => {
    try {
      const token = localStorage.getItem('ownerToken');
      const response = await api.get(
        `/restaurants/${restaurantId}/messages/saved`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSavedMessages(response.data);
    } catch (error) {
      toast.error('Failed to fetch saved messages. Please try again.');

      console.error('Error fetching saved messages:', error);
    }
  };
  useEffect(() => {
    fetchMessages();
    fetchSavedMessages();
  }, [
    restaurantId,
    currentPage,
    messagesPerPage,
    sortField,
    sortDirection,
    statusFilter,
    searchTerm,
    dateFilter,
    savedFilter,
  ]);

  const handleSaveMessage = async (messageId) => {
    try {
      const token = localStorage.getItem('ownerToken');
      // console.log(`Saving message ${messageId}`);
      const response = await api.post(
        `/restaurants/${restaurantId}/messages/${messageId}/save`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // console.log('Save response:', response.data);
      fetchSavedMessages();
      fetchMessages(); // Add this to refresh the main message list
      toast.success('Message saved successfully');
    } catch (error) {
      console.error(
        'Error saving message:',
        error.response?.data || error.message
      );
      toast.error('Failed to save message. Please try again.');
    }
  };

  const handleUnsaveMessage = async (messageId) => {
    try {
      const token = localStorage.getItem('ownerToken');
      await api.post(
        `/restaurants/${restaurantId}/messages/${messageId}/unsave`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchSavedMessages();
      fetchMessages(); // Add this to refresh the main message list
      toast.success('Message unsaved successfully');
    } catch (error) {
      console.error('Error unsaving message:', error);
      toast.error('Failed to unsave message. Please try again.');
    }
  };

  const handleSoftDelete = async (messageId) => {
    try {
      const token = localStorage.getItem('ownerToken');

      await axios.delete(
        `/api/restaurants/${restaurantId}/messages/${messageId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchMessages();
      toast.success('Message deleted successfully');

      // setTextMessages((prev) => prev.filter((msg) => msg._id !== messageId));
    } catch (error) {
      console.error('Error soft deleting text message', error);
      toast.error('Failed to delete message. Please try again.');
    }
  };

  // // Handler functions
  const handleSearchChange = debounce((value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, 300);

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (start, end) => {
    setDateFilter({ start, end });
    setCurrentPage(1);
  };

  const handleSortChange = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleMessagesPerPageChange = (value) => {
    setMessagesPerPage(Number(value));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSavedFilter('all');
    setDateFilter({ start: null, end: null });
    setCurrentPage(1);
    setSortField('scheduledDate');
    setSortDirection('desc');
  };

  const indexOfLastMessage = currentPage * messagesPerPage;
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;

  const handleDelete = async (messageId) => {
    try {
      await axios.delete(`/api/text-messages/${messageId}`);
      setTextMessages((prev) => prev.filter((msg) => msg._id !== messageId));
    } catch (error) {
      console.error('Error deleting text message', error);
    }
  };
  // useEffect(() => {
  //   fetchMessages();
  // }, [
  //   restaurantId,
  //   currentPage,
  //   messagesPerPage,
  //   sortField,
  //   sortDirection,
  //   statusFilter,
  //   searchTerm,
  //   dateFilter,
  //   savedFilter,
  //   savedMessages,
  // ]);

  // const handleParamChange = (e) => {
  //   setParams({ ...params, [e.target.name]: e.target.value, page: 1 });
  // };

  // const debouncedSearch = useCallback(
  //   debounce((value) => {
  //     setParams((prev) => ({ ...prev, search: value, page: 1 }));
  //   }, 300),
  //   []
  // );

  // const handleSearch = (e) => {
  //   debouncedSearch(e.target.value);
  // };

  // const handlePageChange = (page) => {
  //   setParams({ ...params, page });
  // };

  // // State for pagination

  // // Memoized filtered and sorted messages
  // const filteredAndSortedMessages = useMemo(() => {
  //   return textMessages
  //     .filter((message) => {
  //       const matchesSearch =
  //         message.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //         message.content.toLowerCase().includes(searchTerm.toLowerCase());

  //       const matchesStatus =
  //         statusFilter === 'all' || message.status === statusFilter;

  //       const messageDate = new Date(message.scheduledDate);
  //       const matchesDateFilter =
  //         (!dateFilter.start || messageDate >= new Date(dateFilter.start)) &&
  //         (!dateFilter.end || messageDate <= new Date(dateFilter.end));

  //       return matchesSearch && matchesStatus && matchesDateFilter;
  //     })
  //     .sort((a, b) => {
  //       if (a[sortField] < b[sortField])
  //         return sortDirection === 'asc' ? -1 : 1;
  //       if (a[sortField] > b[sortField])
  //         return sortDirection === 'asc' ? 1 : -1;
  //       return 0;
  //     });
  // }, [
  //   textMessages,
  //   searchTerm,
  //   statusFilter,
  //   dateFilter,
  //   sortField,
  //   sortDirection,
  // ]);

  // // Pagination logic

  // const currentMessages = filteredAndSortedMessages.slice(
  //   indexOfFirstMessage,
  //   indexOfLastMessage
  // );
  // const totalPages = Math.ceil(
  //   filteredAndSortedMessages.length / messagesPerPage
  // );

  // useEffect(() => {
  //   const fetchTextMessages = async () => {
  //     try {
  //       setLoading(true);
  //       setError(null);
  //       const token = localStorage.getItem('ownerToken');
  //       const response = await axios.get(
  //         `/api/restaurants/${restaurantId}/messages`,
  //         {
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //           },
  //         }
  //       );
  //       console.log(response.data);
  //       setTextMessages(response.data);
  //       // setFilteredMessages(response.data);
  //     } catch (error) {
  //       console.error('Error fetching text messages', error);
  //       setError('Failed to load messages. Please try again later.');
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchTextMessages();
  // }, [restaurantId]);

  // if (loading) {
  //   return (
  //     <div className='flex justify-center items-center h-screen'>
  //       <div className='animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500'></div>
  //     </div>
  //   );
  // }

  // if (error) {
  //   return (
  //     <div className='flex justify-center items-center h-screen'>
  //       <div className='text-red-500 text-xl'>{error}</div>
  //     </div>
  //   );
  // }

  // const fetchSavedMessages = async () => {
  //   try {
  //     const token = localStorage.getItem('ownerToken');
  //     const response = await api.get(
  //       `/restaurants/${restaurantId}/messages/saved`,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //       }
  //     );
  //     setSavedMessages(response.data);
  //   } catch (error) {
  //     console.error('Error fetching saved messages:', error);
  //   }
  // };

  // useEffect(() => {
  //   fetchMessages();
  //   fetchSavedMessages();
  // }, [restaurantId]);

  return (
    // <div className='container mx-auto py-8'>
    <div className='container mx-auto px-4 py-8'>
      <div className='flex justify-between items-center mb-6'>
        <h1 className='text-3xl font-bold'>Text Messages</h1>
        <button
          className='bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-800 transition duration-300 ease-in-out shadow-md flex items-center'
          onClick={() =>
            navigate(`/restaurant/${restaurantId}/text-messages/new`)
          }
        >
          <FaPlus className='mr-2' />
          Create
        </button>
      </div>
      <p className='text-lg mb-4 text-gray-700'>
        Manage SMS text messages to your subscribers including promotions, news,
        and coupons.
      </p>

      {/* Search Bar */}
      {/* <div className='mb-4 relative'>
        <input
          type='text'
          placeholder='Search messages...'
          className='w-full p-2 pl-10 pr-4 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <FaSearch className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' />
      </div> */}
      <div className='mb-4 relative'>
        <input
          type='text'
          placeholder='Search messages...'
          className='w-full pl-12 pr-4 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <FaSearch className='absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400' />
      </div>

      {/* Filters and Sorting */}
      {/* <div className='mb-4 flex flex-wrap items-center gap-4'>
        <div className='flex items-center'>
          <FaFilter className='mr-2 text-gray-600' />
          <select
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            value={statusFilter}
          >
            <option value='all'>All Statuses</option>
            <option value='scheduled'>Scheduled</option>
            <option value='sent'>Sent</option>
            <option value='failed'>Failed</option>
          </select>
        </div>

        <div className='flex items-center'>
          <label className='mr-2'>From:</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleDateFilterChange(e.target.value, dateFilter.end)
            }
            value={dateFilter.start || ''}
          />
        </div>

        <div className='flex items-center'>
          <label className='mr-2'>To:</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleDateFilterChange(dateFilter.start, e.target.value)
            }
            value={dateFilter.end || ''}
          />
        </div>

        <div className='flex items-center'>
          <FaSort className='mr-2 text-gray-600' />
          <select
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => handleSortChange(e.target.value)}
            value={sortField}
          >
            <option value='title'>Sort by Title</option>
            <option value='scheduledDate'>Sort by Date</option>
            <option value='status'>Sort by Status</option>
          </select>
          <button
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onClick={() =>
              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
            }
          >
            {sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>
        <button
          onClick={resetFilters}
          className='bg-red-500 text-white px-6 py-2 rounded hover:bg-red-700 transition duration-300 ease-in-out shadow-md'
        >
          Clear All Filters
        </button>
      </div> */}

      <div className='mb-4 flex flex-wrap items-center gap-4'>
        <div className='flex items-center'>
          <FaFilter className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            value={statusFilter}
          >
            <option value='all'>All Statuses</option>
            <option value='scheduled'>Scheduled</option>
            <option value='sent'>Sent</option>
            <option value='failed'>Failed</option>
          </select>
        </div>

        <div className='flex items-center'>
          <label className='whitespace-nowrap mr-2'>Saved:</label>
          <select
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => setSavedFilter(e.target.value)}
            value={savedFilter}
          >
            <option value='all'>All Messages</option>
            <option value='saved'>Saved Only</option>
          </select>
        </div>

        <div className='flex items-center'>
          <label className='whitespace-nowrap mr-2'>From:</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleDateFilterChange(e.target.value, dateFilter.end)
            }
            value={dateFilter.start || ''}
          />
        </div>

        <div className='flex items-center'>
          <label className='whitespace-nowrap mr-2'>To:</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleDateFilterChange(dateFilter.start, e.target.value)
            }
            value={dateFilter.end || ''}
          />
        </div>

        <div className='flex items-center'>
          <FaSort className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => handleSortChange(e.target.value)}
            value={sortField}
          >
            <option value='createdAt'>Sort by Creation Date</option>
            <option value='scheduledDate'>Sort by Scheduled Date</option>
            <option value='title'>Sort by Title</option>
            {/* <option value='scheduledDate'>Sort by Date</option> */}
            <option value='status'>Sort by Status</option>
          </select>
          <button
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onClick={() =>
              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
            }
          >
            {sortDirection === 'asc' ? '↑ asc' : '↓ desc'}
          </button>
        </div>

        <button
          onClick={resetFilters}
          className='bg-red-500 text-white px-6 py-2 rounded hover:bg-red-700 transition duration-300 ease-in-out shadow-md'
        >
          Clear All Filters
        </button>
      </div>

      {/* 
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
        <input
          type='text'
          placeholder='Search messages...'
          className='p-2 border rounded'
          onChange={handleSearch}
        />
        <select
          name='status'
          value={params.status}
          onChange={handleParamChange}
          className='p-2 border rounded'
        >
          <option value='all'>All Status</option>
          <option value='scheduled'>Scheduled</option>
          <option value='sent'>Sent</option>
          <option value='failed'>Failed</option>
        </select>
        <select
          name='sortBy'
          value={params.sortBy}
          onChange={handleParamChange}
          className='p-2 border rounded'
        >
          <option value='scheduledDate'>Sort by Date</option>
          <option value='status'>Sort by Status</option>
          <option value='title'>Sort by Title</option>
        </select>
        <select
          name='sortOrder'
          value={params.sortOrder}
          onChange={handleParamChange}
          className='p-2 border rounded'
        >
          <option value='asc'>Ascending</option>
          <option value='desc'>Descending</option>
        </select>

        <div className='flex items-center'>
          <label className='mr-2'>From:</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            // onChange={(e) =>
            //   handleDateFilterChange(e.target.value, dateFilter.end)
            // }
            onChange={handleParamChange}
            value={params.startDate || ''}
          />
        </div>

        <div className='flex items-center'>
          <label className='mr-2'>To:</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            // onChange={(e) =>
            //   handleDateFilterChange(dateFilter.start, e.target.value)
            // }
            // value={dateFilter.end || ''}
            onChange={handleParamChange}
            value={params.endDate || ''}
          />
        </div>
      </div> */}

      {/* <div className='mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'> */}

      {/* <div className='flex justify-between mb-6'>
        <h1 className='text-2xl font-bold  content-center'>Text Messages</h1>
        <button
          className='bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700'
          onClick={() =>
            navigate(`/restaurant/${restaurantId}/text-messages/new`)
          }
        >
          Create New Text Message
        </button>
      </div>
      <h4 className='text-md font-bold mb-4 text-gray-700'>
        Send SMS text message to your subscribers. You can send promotions,
        news, coupons, events, and more!
      </h4>
      */}
      {/* <h1 className='text-2xl font-bold mb-6'>Text Messages</h1>
      <h4 className='text-md font-bold mb-4 text-gray-700'>
        Send SMS text message to your subscribers. You can send promotions,
        news, coupons, events, and more!
      </h4>
      <button
        className='bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700'
        onClick={() =>
          navigate(`/restaurant/${restaurantId}/text-messages/new`)
        }
      >
        Send Text Message
      </button> */}

      {error ? (
        <div className='flex justify-center items-center h-screen'>
          <div className='text-red-500 text-xl'>{error}</div>
        </div>
      ) : loading ? (
        <div className='flex justify-center items-center h-screen'>
          <div className='animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500'></div>
        </div>
      ) : (
        <div className='mt-6'>
          {textMessages.length > 0 ? (
            <div className='grid grid-cols-1 gap-4'>
              {textMessages.map((message) => (
                <div
                  key={message._id}
                  className='bg-white shadow-lg p-4 rounded relative'
                >
                  <button
                    onClick={() =>
                      savedMessages.some((saved) => saved._id === message._id)
                        ? handleUnsaveMessage(message._id)
                        : handleSaveMessage(message._id)
                    }
                    className='absolute top-4 right-4 text-yellow-500 hover:text-yellow-600'
                    title={
                      savedMessages.some((saved) => saved._id === message._id)
                        ? 'Unsave'
                        : 'Save'
                    }
                  >
                    <FaBookmark
                      className={
                        savedMessages.some((saved) => saved._id === message._id)
                          ? 'text-yellow-500'
                          : 'text-gray-300'
                      }
                      size={24}
                    />
                  </button>

                  <h2 className='text-lg font-semibold'>{message.title}</h2>
                  <p className='text-sm font-semibold text-gray-600'>
                    {message.content}
                  </p>
                  <p className='text-sm text-gray-500'>
                    Created: {new Date(message.createdAt).toLocaleString()}
                  </p>
                  <p className='text-sm text-gray-500'>
                    Scheduled Date:{' '}
                    {new Date(message.scheduledDate).toLocaleString()}
                  </p>
                  <p className='text-sm text-gray-500'>
                    Status: {message.status}
                  </p>
                  <p className='text-sm text-gray-500'>
                    Recipients: {message.recipients.length}
                  </p>
                  <p className='text-sm text-gray-500'>
                    Recurrence: {message.recurrence}
                  </p>
                  {message.imageUrl && (
                    <p className='text-sm text-gray-500'>
                      Image:
                      <Link
                        to={message.imageUrl}
                        className='hover:text-blue-600'
                      >
                        {' '}
                        {message.imageUrl}
                      </Link>
                    </p>
                  )}

                  <div className='mt-4 flex space-x-2'>
                    {message.status === 'sent' ? (
                      <>
                        <Link
                          to={`/restaurant/${restaurantId}/messages/${message._id}/edit/view`}
                          className='mr-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded'
                        >
                          View
                        </Link>
                        <Link
                          to={`/restaurant/${restaurantId}/messages/${message._id}/edit/resend`}
                          className='mr-2 bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded'
                        >
                          Send Again
                        </Link>
                      </>
                    ) : (
                      <Link
                        to={`/restaurant/${restaurantId}/messages/${message._id}/edit/resend`}
                        className='mr-2 bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded'
                      >
                        Edit
                      </Link>
                    )}
                    <button
                      onClick={() => handleSoftDelete(message._id)}
                      className='mr-2 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
                    >
                      Delete
                    </button>
                    {message.messageType === 'coupon' && (
                      <button
                        onClick={() => console.log('Coupons!')} // Replace this with your actual event handler
                        className='mr-2 bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded'
                      >
                        Coupons
                      </button>
                    )}

                    {/* {savedMessages.some(
                      (saved) => saved._id === message._id
                    ) ? (
                      <button
                        onClick={() => handleUnsaveMessage(message._id)}
                        className='bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded'
                      >
                        Unsave
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSaveMessage(message._id)}
                        className='bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded'
                      >
                        Save
                      </button>
                    )} */}
                  </div>
                  {/*  */}
                </div>
              ))}
            </div>
          ) : (
            <p>No text messages found for this restaurant.</p>
          )}
        </div>
      )}

      {/* <div className='flex justify-center mt-4'>
        {Array.from({ length: pagination.totalPages }, (_, i) => (
          <button
            key={i}
            onClick={() => handlePageChange(i + 1)}
            className={`mx-1 px-3 py-1 rounded ${
              pagination.currentPage === i + 1
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200'
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div> */}
      {/* Message List */}
      {/* Message List */}
      {/* {filteredAndSortedMessages.length === 0 ? (
        <div className='text-center py-10'>
          <p className='text-xl text-gray-500'>No messages found.</p>
        </div>
      ) : (
        <div className='grid grid-cols-1 gap-4 mb-6'>
          {currentMessages.map((message) => (
            <div key={message._id} className='bg-white shadow-lg p-4 rounded'>
              <h2 className='text-lg font-semibold'>{message.title}</h2>
              <p className='text-sm font-semibold text-gray-600'>
                {message.content}
              </p>
              <p className='text-sm text-gray-500'>
                Scheduled Date:{' '}
                {new Date(message.scheduledDate).toLocaleString()}
              </p>
              <p className='text-sm text-gray-500'>Status: {message.status}</p>
              <p className='text-sm text-gray-500'>
                Recipients: {message.recipients.length}
              </p>
              <p className='text-sm text-gray-500'>
                Recurrence: {message.recurrence}
              </p>
              {message.imageUrl && (
                <p className='text-sm text-gray-500'>
                  Image:
                  <Link to={message.imageUrl} className='hover:text-blue-600'>
                    {' '}
                    {message.imageUrl}
                  </Link>
                </p>
              )}
              <div className='mt-4 flex flex-wrap gap-2'>
                <Link
                  to={`/restaurant/${restaurantId}/messages/${message._id}/edit/view`}
                  className='bg-blue-600 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded flex items-center justify-center min-w-[120px]'
                  title='View Message'
                >
                  <FaEye className='mr-2' />
                  View
                </Link>
                <button
                  onClick={() => handleSoftDelete(message._id)}
                  className='bg-red-600 hover:bg-red-800 text-white font-bold py-2 px-4 rounded flex items-center justify-center min-w-[120px]'
                  title='Delete Message'
                >
                  <FaTrashAlt className='mr-2' />
                  Delete
                </button>
                {message.messageType === 'coupon' && (
                  <button
                    onClick={() => console.log('Coupons!')}
                    className='bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded flex items-center justify-center min-w-[120px]'
                    title='View Coupons'
                  >
                    <FaTags className='mr-2' />
                    Coupons
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )} */}
      {/* Pagination Controls */}
      <div className='flex justify-center mt-5'>
        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(
          (number) => (
            <button
              key={number}
              onClick={() => handlePageChange(number)}
              className={`mx-1 px-3 py-1 rounded ${
                pagination.currentPage === number
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {number}
            </button>
          )
        )}
      </div>

      <div className='flex justify-between items-center mt-4'>
        <div>
          <span className='mr-2'>Messages per page:</span>
          <select
            value={messagesPerPage}
            onChange={(e) => handleMessagesPerPageChange(e.target.value)}
            className='p-2 border rounded'
          >
            <option value='5'>5</option>
            <option value='10'>10</option>
            <option value='20'>20</option>
            <option value='50'>50</option>
          </select>
        </div>

        <div>
          {/* <span>
            Showing {indexOfFirstMessage + 1} -{' '}
            {Math.min(indexOfLastMessage, filteredAndSortedMessages.length)} of{' '}
            {filteredAndSortedMessages.length}
          </span> */}
          <span>
            Showing {indexOfFirstMessage + 1} -{' '}
            {Math.min(indexOfLastMessage, textMessages.length)} of{' '}
            {textMessages.length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TextMessagesPage;

{
  /* <div className='mt-4 flex space-x-2'>
                  {message.status === 'sent' ? (
                    <>
                      <Link
                        to={`/restaurant/${restaurantId}/messages/${message._id}/edit/view`}
                        className='mr-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded'
                      >
                        View
                      </Link>
                      <Link
                        to={`/restaurant/${restaurantId}/messages/${message._id}/edit/resend`}
                        className='mr-2 bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded'
                      >
                        Send Again
                      </Link>
                    </>
                  ) : (
                    <Link
                      to={`/restaurant/${restaurantId}/messages/${message._id}/edit/resend`}
                      className='mr-2 bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded'
                    >
                      Edit
                    </Link>
                  )}
                  <button
                    onClick={() => handleSoftDelete(message._id)}
                    className='mr-2 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
                  >
                    Delete
                  </button>
                  {message.messageType === 'coupon' && (
                    <button
                      onClick={() => console.log('Coupons!')} // Replace this with your actual event handler
                      className='mr-2 bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded'
                    >
                      Coupons
                    </button>
                  )} */
}
{
  /* <button
                    className='bg-red-500 text-white px-2 py-1 rounded'
                    onClick={() => handleSoftDelete(message._id)}
                  >
                    Soft Delete
                  </button>
                  {message.isDeleted && (
                    <button
                      className='bg-gray-500 text-white px-2 py-1 rounded'
                      onClick={() => handleDelete(message._id)}
                    >
                      Delete Permanently
                    </button>
                  )} */
}
{
  /* </div> */
}
